import { useRef } from 'react';
import { makeStyles, DefaultTheme } from '@material-ui/styles';
import cn from 'clsx';

import { UserIcon, UserFullName, UserJobDetails } from '@bizzabo/design-system';
import { UserPreview } from '@bizzabo/entities';
import UserIconButton from './UserIconButton';

type UserIconProps = Pick<React.ComponentProps<typeof UserIcon>, 'size'>;

interface UserProfileProps {
    user: UserPreview;
    userIconSize?: UserIconProps['size'];
    titleVariant?: keyof DefaultTheme['typography'];
    descriptionVariant?: keyof DefaultTheme['typography'];
    isBadgeClickable?: boolean;
    showUserPopup?: (params: {
        id: number;
        avatarRef: React.MutableRefObject<HTMLButtonElement>;
    }) => void;
    notification?: JSX.Element;
    className?: string;
}

const UserProfile: React.FC<UserProfileProps> = ({
    user,
    userIconSize,
    titleVariant,
    descriptionVariant,
    isBadgeClickable,
    notification,
    className,
    showUserPopup,
}) => {
    const classes = useStyles();
    const avatarRef = useRef<HTMLButtonElement>();

    const userClick = () => {
        if (!isBadgeClickable) {
            return;
        }
        showUserPopup({ id: user.userProfileId, avatarRef });
    };

    return (
        <div className={cn(classes.container, className)}>
            <UserIconButton
                userIconSize={userIconSize}
                user={user}
                ref={avatarRef}
                onClick={userClick}
            />
            <div className={classes.details}>
                <div className={classes.nameContainer}>
                    {notification && notification}
                    <UserFullName
                        onClick={userClick}
                        className={isBadgeClickable && classes.userNameClickable}
                        firstName={user.firstName}
                        lastName={user.lastName}
                        variant={titleVariant}
                    />
                </div>
                <UserJobDetails
                    role={user.title}
                    company={user.company}
                    variant={descriptionVariant}
                />
            </div>
        </div>
    );
};

const useStyles = makeStyles(
    theme => ({
        container: {
            display: 'flex',
            alignItems: 'center',
        },
        details: {
            marginLeft: 8,
        },
        userNameClickable: {
            cursor: 'pointer',
            position: 'relative',
            '&:hover': {
                '&:after': {
                    content: '""',
                    position: 'absolute',
                    backgroundColor: theme.palette.greys[500],
                    height: 1,
                    left: 0,
                    right: 0,
                    bottom: 0,
                },
            },
        },
        nameContainer: {
            display: 'inline-flex',
            position: 'relative',
            '& > div': {
                display: 'inline-flex',
            },
        },
    }),
    { classNamePrefix: UserProfile.name }
);

export default UserProfile;
